import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import QRCode from "qrcode.react";
import { FaExternalLinkAlt } from "react-icons/fa";

import HotelImage from "../components/HotelImage";
import ReservationHeader from "../components/ReservationHeader";
import { ReservationInfoResponse } from "../types/reservation";
import Button from "../components/common/Button";
import ReservationInfo from "../components/ReservationInfo";
import { ROUTE_PATH } from "../constants";
import RoomName from "../components/RoomName";
import List from "../components/common/List";
import KeyInfoItem from "../components/KeyInfoItem";
import { checkOut, getAnnouncements, getCheckOutBalance } from "../api";
import Spinner from "../components/common/Spinner";
import Notification from "../components/Notification";
import Modal from "../components/common/Modal";
import { FaChevronDown } from "react-icons/fa";
import { AnnouncementResponse, ConfigResponse } from "../types";
import { useTranslation } from "react-i18next";
import BookingButton from "../components/BookingButton";

interface Props {
  config: ConfigResponse;
  reservationInfo: ReservationInfoResponse;
}

interface HideState {
  [key: string]: boolean;
}

const linkifyText = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;

  return text.split(urlRegex).map((part, index) => {
    if (urlRegex.test(part)) {
      return (
        <a key={index} href={part} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
          {part}
        </a>
      );
    }
    return part;
  });
};

const CheckInPage = ({ config, reservationInfo }: Props) => {
  const [announcements, setAnnouncements] = useState<AnnouncementResponse[]>([]);
  const [openIndices, setOpenIndices] = useState<Set<number>>(new Set());
  const [isCheckOutModal, setIsCheckOutModal] = useState(false);
  const [isHide, setIsHide] = useState<HideState>({
    pincode: false,
    exchangeKey: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const {
    room_no,
    guest_name,
    key_info: { pincode, exchange_key, qr_data }
  } = reservationInfo;

  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleAccordionClick = (index: number) => {
    setOpenIndices((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  };

  const handleToggleCheckoutModal = () => {
    setIsCheckOutModal((prev) => !prev);
  };

  const handleToggleHide = (target: string) => {
    setIsHide((prev) => ({ ...prev, [target]: !prev[target] }));
  };

  const handleCheckOut = async (balance_amount: number) => {
    const { status } = await checkOut({ balance_amount });

    if (status === 200) {
      navigate(ROUTE_PATH.CHECKOUT);
    }
  };

  const handleCheckBalance = async () => {
    try {
      setIsLoading(true);

      const {
        data: { balance_amount }
      } = await getCheckOutBalance();

      if (balance_amount === 0) {
        await handleCheckOut(balance_amount);
      } else {
        setErrorMessage(t("unpaid_balance"));
        setIsError(true);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error("checkout error:", error);
        setErrorMessage(error.response?.data.message);
        setIsError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const { data } = await getAnnouncements();
        setAnnouncements(data);
        setOpenIndices(new Set(data.map((_: AnnouncementResponse, i: number) => i)));
      } catch (error) {
        if (error instanceof AxiosError) {
          setErrorMessage(error.response?.data.message);
          setIsError(true);
        }
      }
    };
    fetchAnnouncements();
  }, []);

  if (isLoading) return <Spinner />;
  if (isError) return <Notification message={errorMessage} />;

  return (
    <>
      <div className="flex flex-col gap-y-6">
        <ReservationHeader hotelName={reservationInfo.hotel_name} guestName={guest_name} />
        <HotelImage hotelImage={config.hotel_image} />
        <div>
          <RoomName roomName={room_no} />
          <List>
            {pincode && (
              <KeyInfoItem
                label={t("password")}
                isHide={isHide.pincode}
                value={`${pincode}*`}
                handleToggleHide={() => handleToggleHide("pincode")}
              />
            )}
            {config.is_mobile_key && exchange_key && (
              <KeyInfoItem
                label={
                  <>
                    {t("mobile_key")}
                    <a href="https://ovalmms.page.link/key">
                      <FaExternalLinkAlt size={14} color="#3b82f6" />
                    </a>
                  </>
                }
                labelClassName="flex items-center gap-x-2"
                isHide={isHide.exchangeKey}
                value={exchange_key || "12345"}
                handleToggleHide={() => handleToggleHide("exchangeKey")}
              />
            )}
            {qr_data && (
              <div className="flex flex-col justify-center items-center gap-2 mt-3">
                <span className="text-sm font-semibold">{`${t("elevator")} / ${t("public_facilities")} QR`}</span>
                <QRCode value={qr_data} size={190} />
              </div>
            )}
          </List>
        </div>
        <ReservationInfo reservationInfo={reservationInfo} />

        {/* 공지사항 아코디언 영역 */}
        {announcements.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold py-4">{t("notice")}</h3>
            <div className="flex flex-col gap-y-2">
              {announcements.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    className="w-full flex justify-between items-center p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                    onClick={() => handleAccordionClick(index)}
                  >
                    <span className="font-semibold text-sm text-gray-800">{item.title}</span>
                    <span
                      className={`transform transition-transform duration-200 ${openIndices.has(index) ? "rotate-180" : ""}`}
                    >
                      <FaChevronDown size={14} color="#6b7280" />
                    </span>
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      openIndices.has(index) ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="p-4 text-sm text-gray-600 bg-white border-t border-gray-100 break-words max-h-60 overflow-y-auto">
                      {linkifyText(item.content)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-y-4">
          {config.is_check_out && <Button onClick={handleToggleCheckoutModal}>{t("checkout")}</Button>}
          <BookingButton />
        </div>
      </div>

      {isCheckOutModal && (
        <Modal
          onClose={handleToggleCheckoutModal}
          title={t("checkout_confirm")}
          bottom={
            <div className="space-y-3">
              <Button onClick={handleCheckBalance}>{t("confirm")}</Button>
              <Button variant="outlined" onClick={handleToggleCheckoutModal}>
                {t("cancel")}
              </Button>
            </div>
          }
        >
          <p className="text-sm text-gray-600 text-center">{t("checkout_noReentry")}</p>
        </Modal>
      )}
    </>
  );
};

export default CheckInPage;
